import { Box } from '@deckee/deck-hand';
import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectUnitsConfig } from '../../../../../services/store/modules/app-controls';
import { TideChart } from '../charts';
import {
  buildTideEventAnnotations,
  enrichedSeriesToTimeSeries,
  formatTideHeightDisplay,
  getClosestEnrichedPoint,
  getForecastDayDomain,
  getTideTrendLabel,
  graphPointsToEnrichedSeries,
  type WeatherForecastGraphPoint,
} from '../../helpers/graph/graphData';
import { useGraphTime } from './GraphTimeContext';
import CollapsibleCard from '../CollapsibleCard/CollapsibleCard';
import GraphCardHeader from './GraphCardHeader';
import GraphCardNoData from './GraphCardNoData';

export interface TideCardProps {
  tidePoints: WeatherForecastGraphPoint[];
  selectedDate: string;
  timeZone: string;
  isToday: boolean;
  latitude: number;
  longitude: number;
  locationName?: string;
  footerText?: string;
}

const TideCard: FC<TideCardProps> = ({
  tidePoints,
  selectedDate,
  timeZone,
  isToday,
  latitude,
  longitude,
  locationName,
  footerText,
}) => {
  const unitsConfig = useSelector(selectUnitsConfig);
  const {
    selectedChartTime,
    defaultChartTime,
    handleSelectedChartTimeChange,
    handleChartScrubStart,
    handleChartScrubEnd,
  } = useGraphTime('tide');

  const tideSeries = useMemo(
    () => graphPointsToEnrichedSeries(tidePoints, selectedDate, timeZone),
    [selectedDate, tidePoints, timeZone],
  );

  const domain = useMemo(
    () => getForecastDayDomain(selectedDate, timeZone),
    [selectedDate, timeZone],
  );

  const chartData = useMemo(
    () => enrichedSeriesToTimeSeries(tideSeries, domain),
    [domain, tideSeries],
  );

  const annotations = useMemo(
    () => buildTideEventAnnotations(tidePoints, selectedDate, timeZone),
    [selectedDate, tidePoints, timeZone],
  );

  const selectedTidePoint = useMemo(() => {
    if (!selectedChartTime) {
      return undefined;
    }

    return getClosestEnrichedPoint(tideSeries, selectedChartTime);
  }, [selectedChartTime, tideSeries]);

  const tideTrend = getTideTrendLabel(chartData, selectedChartTime);

  const primaryDisplay = selectedTidePoint
    ? formatTideHeightDisplay(selectedTidePoint.value, unitsConfig)
    : '—';

  if (chartData.length === 0) {
    return (
      <CollapsibleCard
        title="Tide"
        testId="weather-forecast-tide-card"
        analyticsCard="tide"
      >
        <GraphCardNoData
          chartLabel="Tide"
          stationType="tides"
          isLocationMissingData={tidePoints.length === 0}
          latitude={latitude}
          longitude={longitude}
          selectedDate={selectedDate}
          locationName={locationName}
        />
      </CollapsibleCard>
    );
  }

  return (
    <CollapsibleCard
      title="Tide"
      testId="weather-forecast-tide-card"
      analyticsCard="tide"
    >
      <GraphCardHeader primary={primaryDisplay} secondary={tideTrend} />

      <Box width="100%">
        <TideChart
          data={chartData}
          domain={domain}
          timeZone={timeZone}
          annotations={annotations}
          selectedTime={selectedChartTime}
          defaultSelectedTime={defaultChartTime}
          onSelectedTimeChange={handleSelectedChartTimeChange}
          onScrubStart={handleChartScrubStart}
          onScrubEnd={handleChartScrubEnd}
        />
      </Box>
      {footerText && (
        <Box fontSize="11px" color="darkergrey" mt={2}>
          {footerText}
        </Box>
      )}
    </CollapsibleCard>
  );
};

export default TideCard;
